import React, { useEffect, useMemo, useState } from 'react';
import { SVG_VARIANTS } from './index';
import { DEFAULTS } from './EditSchema';

const COLOR_MAP = {
  black: '#1b130c',
  petrol: '#008297',
  white: '#ffffff',
  purple: '#82378c',
};

function applyRowColorsToSvg(
  svgUrl,
  { row1Color, row2Color, row3Color },
  setSvgContent,
) {
  const rowColors = {
    1: COLOR_MAP[row1Color] || COLOR_MAP.black,
    2: COLOR_MAP[row2Color] || COLOR_MAP.black,
    3: COLOR_MAP[row3Color] || COLOR_MAP.black,
  };

  fetch(svgUrl)
    .then((res) => res.text())
    .then((svgText) => {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(svgText, 'image/svg+xml');

      [1, 2, 3].forEach((row) => {
        const elements = xmlDoc.querySelectorAll(`.row-${row}`);
        elements.forEach((el) => {
          el.style.fill = rowColors[row];
        });
      });

      setSvgContent(xmlDoc.documentElement.outerHTML);
    })
    .catch(() => {
      setSvgContent(null);
    });
}

const BrandView = (props) => {
  const { data = {} } = props;

  const behavior = data.behavior || DEFAULTS.behavior;
  const intervalSeconds = data.intervalSeconds || DEFAULTS.intervalSeconds;
  const columns = data.columns || DEFAULTS.columns;
  const columnsCount = columns === 'three' ? 3 : columns === 'two' ? 2 : 1;

  const row1ColorLeft =
    data.row1ColorLeft || data.row1Color || DEFAULTS.row1ColorLeft;
  const row2ColorLeft =
    data.row2ColorLeft || data.row2Color || DEFAULTS.row2ColorLeft;
  const row3ColorLeft =
    data.row3ColorLeft || data.row3Color || DEFAULTS.row3ColorLeft;

  const row1ColorRight =
    data.row1ColorRight || data.row1Color || DEFAULTS.row1ColorRight;
  const row2ColorRight =
    data.row2ColorRight || data.row2Color || DEFAULTS.row2ColorRight;
  const row3ColorRight =
    data.row3ColorRight || data.row3Color || DEFAULTS.row3ColorRight;

  const row1ColorThird =
    data.row1ColorThird ||
    data.row1ColorRight ||
    data.row1ColorLeft ||
    data.row1Color ||
    DEFAULTS.row1ColorThird;
  const row2ColorThird =
    data.row2ColorThird ||
    data.row2ColorRight ||
    data.row2ColorLeft ||
    data.row2Color ||
    DEFAULTS.row2ColorThird;
  const row3ColorThird =
    data.row3ColorThird ||
    data.row3ColorRight ||
    data.row3ColorLeft ||
    data.row3Color ||
    DEFAULTS.row3ColorThird;

  const variants = useMemo(() => SVG_VARIANTS || [], []);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [leftSvg, setLeftSvg] = useState(null);
  const [secondSvg, setSecondSvg] = useState(null);
  const [thirdSvg, setThirdSvg] = useState(null);

  useEffect(() => {
    if (!variants.length) return;

    if (behavior === 'reload') {
      const randomIndex = Math.floor(Math.random() * variants.length);
      setCurrentIndex(randomIndex);
    } else {
      setCurrentIndex(0);
    }
  }, [behavior, variants]);

  useEffect(() => {
    if (behavior !== 'interval' || !variants.length) return;

    const sec = Math.max(1, intervalSeconds || DEFAULTS.intervalSeconds);

    const interval = setInterval(() => {
      setCurrentIndex((prev) => {
        if (!variants.length) return 0;
        let next = Math.floor(Math.random() * variants.length);
        if (variants.length > 1 && next === prev) {
          next = (next + 1) % variants.length;
        }
        return next;
      });
    }, sec * 1000);

    return () => clearInterval(interval);
  }, [behavior, intervalSeconds, variants]);

  useEffect(() => {
    if (!variants.length) return;

    const indices = [currentIndex];
    while (indices.length < columnsCount && variants.length) {
      let next = Math.floor(Math.random() * variants.length);
      if (variants.length > indices.length && indices.includes(next)) {
        next = (next + 1) % variants.length;
      }

      if (!indices.includes(next) || variants.length <= indices.length) {
        indices.push(next);
      }
    }

    const [leftIndex, secondIndex, thirdIndex] = indices;

    applyRowColorsToSvg(
      variants[leftIndex],
      {
        row1Color: row1ColorLeft,
        row2Color: row2ColorLeft,
        row3Color: row3ColorLeft,
      },
      setLeftSvg,
    );

    if (columnsCount >= 2) {
      applyRowColorsToSvg(
        variants[secondIndex ?? leftIndex],
        {
          row1Color: row1ColorRight,
          row2Color: row2ColorRight,
          row3Color: row3ColorRight,
        },
        setSecondSvg,
      );
    } else {
      setSecondSvg(null);
    }

    if (columnsCount === 3) {
      applyRowColorsToSvg(
        variants[thirdIndex ?? leftIndex],
        {
          row1Color: row1ColorThird,
          row2Color: row2ColorThird,
          row3Color: row3ColorThird,
        },
        setThirdSvg,
      );
    } else {
      setThirdSvg(null);
    }
  }, [
    variants,
    currentIndex,
    columnsCount,
    row1ColorLeft,
    row2ColorLeft,
    row3ColorLeft,
    row1ColorRight,
    row2ColorRight,
    row3ColorRight,
    row1ColorThird,
    row2ColorThird,
    row3ColorThird,
  ]);

  if (!leftSvg) {
    return <div>Lade Raster …</div>;
  }

  const columnsClass =
    columnsCount === 3
      ? 'columns-three'
      : columnsCount === 2
        ? 'columns-two'
        : 'columns-one';

  return (
    <div className={`fcmh-raster-block ${columnsClass}`}>
      <div className="fcmh-raster-row">
        <div
          className="fcmh-raster-svg-wrapper"
          dangerouslySetInnerHTML={{ __html: leftSvg }}
        />
        {columnsCount >= 2 && secondSvg && (
          <div
            className="fcmh-raster-svg-wrapper"
            dangerouslySetInnerHTML={{ __html: secondSvg }}
          />
        )}
        {columnsCount === 3 && thirdSvg && (
          <div
            className="fcmh-raster-svg-wrapper"
            dangerouslySetInnerHTML={{ __html: thirdSvg }}
          />
        )}
      </div>
    </div>
  );
};

export default BrandView;
